import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BehaviorSubject, from } from 'rxjs';
import { skip } from 'rxjs/operators';
import { TaskIdV2, UserId } from 'api/models';
import { TimeEntriesApi } from 'api/timesheets/time-entries';
import { date as dateByDay, DayOfWeek } from 'core/day-of-week';
import { Empty, Loaded, Loading, Refreshing } from 'core/state';
import { useUser } from 'hooks/use-user';
import { logger } from 'services';

type State = Empty | Loading | Loaded<number> | Refreshing<number>;

export const useTimeSpent = ([, taskId]: TaskIdV2) => {
  const timeSpent$ = useRef(new BehaviorSubject<State>(Empty()));
  const [timeSpent, setTimeSpent] = useState<State>(Empty());
  const [loaded, setLoaded] = useState(false);
  const {
    user: { userId },
  } = useUser();
  const refresh = useCallback(() => {
    setLoaded(false);
  }, [setLoaded]);

  useEffect(() => {
    setLoaded(false);
  }, [taskId, userId, setLoaded]);

  useEffect(() => {
    timeSpent$.current.pipe(skip(1)).subscribe(setTimeSpent);
  }, [timeSpent$, setTimeSpent]);

  useEffect(() => {
    if (loaded) {
      return () => {};
    }

    const loading =
      timeSpent$.current.value[1] != null
        ? Refreshing(timeSpent$.current.value[1])
        : Loading();

    timeSpent$.current.next(loading);
    const subscription = from(
      TimeEntriesApi.getTaskTimeEntriesForUser(
        UserId(userId),
        TaskIdV2(taskId),
        dateByDay(DayOfWeek.Monday, moment())
          .add(6, 'days')
          .format('YYYY-MM-DD'),
      ),
    ).subscribe(
      (entries) =>
        timeSpent$.current.next(
          Loaded(entries.map((x) => x.minutes).reduce((x, y) => x + y, 0)),
        ),
      (error: Error) => {
        logger.error('fetching time spent on task failed', {
          error,
          taskId,
          userId,
        });
        timeSpent$.current.next(Empty());
      },
      () => setLoaded(true),
    );

    return () => subscription.unsubscribe();
  }, [loaded, taskId, userId, timeSpent$]);

  return { timeSpent, refresh };
};
